import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Brain,
  Settings,
  Plus,
  Trash2,
  Check,
  X,
  RefreshCcw,
  Server,
  Cloud,
  Key,
  Sparkles,
  Zap,
  ExternalLink,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  Loader2,
  Save,
  Edit } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const providerIcons = {
  ollama: Server,
  openrouter: Cloud,
  anthropic: Brain,
  gemini: Sparkles,
  openai: Zap
};

const providerColors = {
  ollama: 'from-gray-600 to-gray-800',
  openrouter: 'from-violet-500 to-purple-600',
  anthropic: 'from-amber-500 to-orange-600',
  gemini: 'from-blue-500 to-cyan-500',
  openai: 'from-emerald-500 to-green-600'
};

export const AISettings = () => {const { t } = useTranslation("cns");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [providers, setProviders] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);

  // Form state
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [customBaseUrl, setCustomBaseUrl] = useState('');

  // Custom provider/model state
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [showAddModel, setShowAddModel] = useState(false);
  const [newProvider, setNewProvider] = useState({
    id: '',
    name: '',
    type: 'cloud',
    requires_api_key: true,
    base_url: ''
  });
  const [newModel, setNewModel] = useState({
    provider_id: '',
    id: '',
    name: '',
    context_length: 4096
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cns/ai/providers`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
        setActiveConfig(data.active_config);

        // Set form values if config exists
        if (data.active_config?.provider) {
          setSelectedProvider(data.active_config.provider);
          setSelectedModel(data.active_config.model || '');
        }
      }
    } catch (err) {
      console.error('Failed to fetch AI providers');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedProviderData = () => {
    return providers.find((p) => p.id === selectedProvider);
  };

  const handleSaveConfig = async () => {
    if (!selectedProvider || !selectedModel) {
      toast.error('Please select a provider and model');
      return;
    }

    const provider = getSelectedProviderData();
    if (provider?.requires_api_key && !apiKey && !activeConfig?.has_api_key) {
      toast.error('API key is required for this provider');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/cns/ai/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          provider_id: selectedProvider,
          model_id: selectedModel,
          api_key: apiKey || null,
          base_url: customBaseUrl || null
        })
      });

      if (res.ok) {
        toast.success('AI configuration saved!');
        fetchProviders();
        setApiKey(''); // Clear API key from form
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedProvider) {
      toast.error('Please select a provider first');
      return;
    }

    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/cns/ai/test-connection`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          provider_id: selectedProvider,
          model_id: selectedModel,
          api_key: apiKey || null,
          base_url: customBaseUrl || null
        })
      });

      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        if (data.available_models) {
          toast.info(`Available models: ${data.available_models.slice(0, 5).join(', ')}${data.available_models.length > 5 ? '...' : ''}`);
        }
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleAddProvider = async () => {
    if (!newProvider.id || !newProvider.name || !newProvider.base_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cns/ai/providers/custom`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newProvider)
      });

      if (res.ok) {
        toast.success('Custom provider added!');
        fetchProviders();
        setShowAddProvider(false);
        setNewProvider({ id: '', name: '', type: 'cloud', requires_api_key: true, base_url: '' });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add provider');
      }
    } catch (err) {
      toast.error('Failed to add provider');
    }
  };

  const handleAddModel = async () => {
    if (!newModel.provider_id || !newModel.id || !newModel.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cns/ai/models/custom`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newModel)
      });

      if (res.ok) {
        toast.success('Custom model added!');
        fetchProviders();
        setShowAddModel(false);
        setNewModel({ provider_id: '', id: '', name: '', context_length: 4096 });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add model');
      }
    } catch (err) {
      toast.error('Failed to add model');
    }
  };

  const handleDeleteCustomModel = async (providerId, modelId) => {
    try {
      const res = await fetch(`${API_URL}/api/cns/ai/models/custom/${providerId}/${modelId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Custom model removed');
        fetchProviders();
      }
    } catch (err) {
      toast.error('Failed to remove model');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500" />
      </div>);

  }

  const selectedProviderData = getSelectedProviderData();

  return (
    <div className="space-y-6" data-testid="ai-settings">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl">
              <Brain className="h-7 w-7 text-white" />
            </div>{t("aiConfiguration", "AI Configuration")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureYourAi", "Configure your AI provider for content generation")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeConfig?.provider &&
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
              <CheckCircle className="h-3 w-3" />{t("configured", "Configured:")}
            {activeConfig.provider} / {activeConfig.model}
            </Badge>
          }
        </div>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="h-4 w-4" />{t("configuration", "Configuration")}
          </TabsTrigger>
          <TabsTrigger value="providers" className="gap-2">
            <Server className="h-4 w-4" />{t("providersModels", "Providers & Models")}
          </TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-6 mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Provider Selection */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="h-5 w-5 text-violet-600" />{t("selectProvider", "Select Provider")}

                </CardTitle>
                <CardDescription>{t("chooseYourAi", "Choose your AI provider. Local providers don't need API keys.")}

                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3">
                  {providers.map((provider) => {
                    const Icon = providerIcons[provider.id] || Cloud;
                    const colors = providerColors[provider.id] || 'from-gray-500 to-gray-700';
                    const isSelected = selectedProvider === provider.id;

                    return (
                      <div
                        key={provider.id}
                        onClick={() => {
                          setSelectedProvider(provider.id);
                          setSelectedModel('');
                          setApiKey('');
                          setCustomBaseUrl('');
                        }}
                        className={`
                          p-4 rounded-lg border-2 cursor-pointer transition-all
                          ${isSelected ?
                        'border-violet-500 bg-violet-50 shadow-md' :
                        'border-border hover:border-violet-200 hover:bg-muted/50'}
                        `
                        }>
                        
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${colors} text-white`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{provider.name}</span>
                              {!provider.requires_api_key &&
                              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 border-emerald-200">{t("noApiKey", "No API Key")}

                              </Badge>
                              }
                              {provider.type === 'local' &&
                              <Badge variant="outline" className="text-xs">{t("local", "Local")}</Badge>
                              }
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {provider.models?.length || 0}{t("modelsAvailable", "models available")}
                            </p>
                          </div>
                          {isSelected &&
                          <CheckCircle className="h-5 w-5 text-violet-600" />
                          }
                        </div>
                      </div>);

                  })}
                </div>
              </CardContent>
            </Card>

            {/* Model & API Key */}
            <div className="space-y-6">
              {/* Model Selection */}
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-violet-600" />{t("selectModel", "Select Model")}

                  </CardTitle>
                  <CardDescription>{t("chooseASpecific", "Choose a specific model from the selected provider")}

                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {selectedProvider ?
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedProviderData?.models?.map((model) =>
                    <div
                      key={model.id}
                      onClick={() => setSelectedModel(model.id)}
                      className={`
                            p-3 rounded-lg border cursor-pointer transition-all
                            ${selectedModel === model.id ?
                      'border-violet-500 bg-violet-50' :
                      'border-border hover:border-violet-200'}
                          `
                      }>
                      
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-sm">{model.name}</span>
                              {model.custom &&
                          <Badge variant="outline" className="ml-2 text-xs">{t("custom", "Custom")}</Badge>
                          }
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {(model.context_length / 1000).toFixed(0)}{t("kCtx", "K ctx")}
                        </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 font-mono">
                            {model.id}
                          </p>
                        </div>
                    )}
                    </div> :

                  <div className="text-center py-8 text-muted-foreground">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t("selectAProvider", "Select a provider first")}</p>
                    </div>
                  }
                </CardContent>
              </Card>

              {/* API Key / Configuration */}
              {selectedProvider && selectedProviderData?.requires_api_key &&
              <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="h-5 w-5 text-violet-600" />{t("apiKey", "API Key")}

                  </CardTitle>
                    <CardDescription>{t("enterYourApi", "Enter your API key for")}
                    {selectedProviderData.name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t("apiKey", "API Key")}</Label>
                      <Input
                      type="password"
                      placeholder="Enter your API key..."
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)} />
                    
                      {activeConfig?.provider === selectedProvider && activeConfig?.has_api_key &&
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />{t("apiKeyAlready", "API key already configured (leave empty to keep current)")}

                    </p>
                    }
                    </div>
                    <div className="space-y-2">
                      <Label>{t("customBaseUrl", "Custom Base URL (Optional)")}</Label>
                      <Input
                      placeholder={selectedProviderData.base_url}
                      value={customBaseUrl}
                      onChange={(e) => setCustomBaseUrl(e.target.value)} />
                    
                      <p className="text-xs text-muted-foreground">{t("overrideTheDefault", "Override the default API endpoint if needed")}

                    </p>
                    </div>
                  </CardContent>
                </Card>
              }

              {/* Local Provider Config */}
              {selectedProvider && selectedProviderData && !selectedProviderData.requires_api_key &&
              <Card className="card-elevated border-emerald-200 bg-emerald-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-emerald-700">{t("noApiKey", "No API Key Required")}</p>
                        <p className="text-sm text-emerald-600 mt-1">{t("thisIsA", "This is a local provider. Make sure")}
                        {selectedProviderData.name}{t("isRunningAt", "is running at:")}
                      </p>
                        <div className="mt-2 space-y-2">
                          <Input
                          placeholder={selectedProviderData.base_url}
                          value={customBaseUrl || selectedProviderData.base_url}
                          onChange={(e) => setCustomBaseUrl(e.target.value)} />
                        
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              }
            </div>
          </div>

          {/* Action Buttons */}
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {selectedProvider && selectedModel ?
                  <span>{t("readyToSave", "Ready to save:")}
                    <strong>{selectedProviderData?.name}</strong> / <strong>{selectedModel}</strong>
                    </span> :

                  <span>{t("selectAProvider", "Select a provider and model to continue")}</span>
                  }
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={!selectedProvider || testing}
                    className="gap-2">
                    
                    {testing ?
                    <Loader2 className="h-4 w-4 animate-spin" /> :

                    <RefreshCcw className="h-4 w-4" />
                    }{t("testConnection", "Test Connection")}

                  </Button>
                  <Button
                    onClick={handleSaveConfig}
                    disabled={!selectedProvider || !selectedModel || saving}
                    className="gap-2 bg-gradient-to-r from-violet-500 to-purple-600">
                    
                    {saving ?
                    <Loader2 className="h-4 w-4 animate-spin" /> :

                    <Save className="h-4 w-4" />
                    }{t("saveConfiguration", "Save Configuration")}

                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Providers & Models Tab */}
        <TabsContent value="providers" className="space-y-6 mt-6">
          {/* Add Custom Provider */}
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t("customProviders", "Custom Providers")}</CardTitle>
                  <CardDescription>{t("addYourOwn", "Add your own AI providers")}</CardDescription>
                </div>
                <Button onClick={() => setShowAddProvider(true)} className="gap-2">
                  <Plus className="h-4 w-4" />{t("addProvider", "Add Provider")}
                </Button>
              </div>
            </CardHeader>
            {showAddProvider &&
            <CardContent className="border-t">
                <div className="grid gap-4 md:grid-cols-2 pt-4">
                  <div className="space-y-2">
                    <Label>{t("providerId", "Provider ID")}</Label>
                    <Input
                    placeholder="my-provider"
                    value={newProvider.id}
                    onChange={(e) => setNewProvider({ ...newProvider, id: e.target.value })} />
                  
                  </div>
                  <div className="space-y-2">
                    <Label>{t("displayName", "Display Name")}</Label>
                    <Input
                    placeholder="My Custom Provider"
                    value={newProvider.name}
                    onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })} />
                  
                  </div>
                  <div className="space-y-2">
                    <Label>{t("baseUrl", "Base URL")}</Label>
                    <Input
                    placeholder="http://localhost:8080"
                    value={newProvider.base_url}
                    onChange={(e) => setNewProvider({ ...newProvider, base_url: e.target.value })} />
                  
                  </div>
                  <div className="space-y-2">
                    <Label>{t("type", "Type")}</Label>
                    <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newProvider.type}
                    onChange={(e) => setNewProvider({ ...newProvider, type: e.target.value })}>
                    
                      <option value="local">{t("local", "Local")}</option>
                      <option value="cloud">{t("cloud", "Cloud")}</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 md:col-span-2">
                    <Switch
                    checked={newProvider.requires_api_key}
                    onCheckedChange={(checked) => setNewProvider({ ...newProvider, requires_api_key: checked })} />
                  
                    <Label>{t("requiresApiKey", "Requires API Key")}</Label>
                  </div>
                  <div className="flex gap-2 md:col-span-2">
                    <Button variant="outline" onClick={() => setShowAddProvider(false)}>{t("cancel", "Cancel")}</Button>
                    <Button onClick={handleAddProvider}>{t("addProvider", "Add Provider")}</Button>
                  </div>
                </div>
              </CardContent>
            }
          </Card>

          {/* Add Custom Model */}
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t("customModels", "Custom Models")}</CardTitle>
                  <CardDescription>{t("addCustomModels", "Add custom models to existing providers")}</CardDescription>
                </div>
                <Button onClick={() => setShowAddModel(true)} className="gap-2">
                  <Plus className="h-4 w-4" />{t("addModel", "Add Model")}
                </Button>
              </div>
            </CardHeader>
            {showAddModel &&
            <CardContent className="border-t">
                <div className="grid gap-4 md:grid-cols-2 pt-4">
                  <div className="space-y-2">
                    <Label>{t("provider", "Provider")}</Label>
                    <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newModel.provider_id}
                    onChange={(e) => setNewModel({ ...newModel, provider_id: e.target.value })}>
                    
                      <option value="">{t("selectProvider", "Select provider...")}</option>
                      {providers.map((p) =>
                    <option key={p.id} value={p.id}>{p.name}</option>
                    )}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("modelId", "Model ID")}</Label>
                    <Input
                    placeholder="my-model-7b"
                    value={newModel.id}
                    onChange={(e) => setNewModel({ ...newModel, id: e.target.value })} />
                  
                  </div>
                  <div className="space-y-2">
                    <Label>{t("displayName", "Display Name")}</Label>
                    <Input
                    placeholder="My Model 7B"
                    value={newModel.name}
                    onChange={(e) => setNewModel({ ...newModel, name: e.target.value })} />
                  
                  </div>
                  <div className="space-y-2">
                    <Label>{t("contextLength", "Context Length")}</Label>
                    <Input
                    type="number"
                    placeholder="4096"
                    value={newModel.context_length}
                    onChange={(e) => setNewModel({ ...newModel, context_length: parseInt(e.target.value) })} />
                  
                  </div>
                  <div className="flex gap-2 md:col-span-2">
                    <Button variant="outline" onClick={() => setShowAddModel(false)}>{t("cancel", "Cancel")}</Button>
                    <Button onClick={handleAddModel}>{t("addModel", "Add Model")}</Button>
                  </div>
                </div>
              </CardContent>
            }
          </Card>

          {/* All Providers List */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {providers.map((provider) => {
              const Icon = providerIcons[provider.id] || Cloud;
              const colors = providerColors[provider.id] || 'from-gray-500 to-gray-700';

              return (
                <Card key={provider.id} className="card-elevated">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${colors} text-white`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{provider.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {provider.type === 'local' ? 'Local' : 'Cloud'} • 
                          {provider.requires_api_key ? ' API Key Required' : ' No API Key'}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground mb-2 font-mono truncate">
                      {provider.base_url}
                    </p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {provider.models?.map((model) =>
                      <div key={model.id} className="flex items-center justify-between text-xs p-1.5 rounded bg-muted/50">
                          <span className="truncate flex-1">{model.name}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">
                              {(model.context_length / 1000).toFixed(0)}K
                            </span>
                            {model.custom &&
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => handleDeleteCustomModel(provider.id, model.id)}>
                            
                                <Trash2 className="h-3 w-3 text-red-500" />
                              </Button>
                          }
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>);

            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>);

};

export default AISettings;